/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { PlayablePrayer } from "@packages/sdk";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  colors,
  numericPixels,
  spacing,
  stroke,
} from "../../../../../../global/emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";
import { Carousel } from "../../../../../components/blocks/layout/Carousel/Carousel.emotion";
import { Thumbnail } from "../../../../../components/blocks/other/Thumbnail/Thumbnail.emotion";
import { useCarousel } from "../../../../../hooks/useCarousel";
import { useIntersectionObserverRefState } from "../../../../../hooks/useIntersectionObserverRefState";
import { useIsDesktopViewport } from "../../../../../hooks/useIsDesktopViewport";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../../../../../lib/constants/endOfBrowserAsyncQueue.constants";
import { emotionDynamics } from "../../../../../lib/emotionDynamics";

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  gap: spacing.ms,
  justifyContent: "center",
  maxWidth: numericPixels[360],
  width: spacing.full,
};
const buttonStyle: Interpolation = {
  background: colors.transparent,
  border: spacing.none,
  outline: "none",
  padding: spacing.none,
  transform: "scale(1)",
  transition: "transform 50ms ease-out",
};
const thumbnailStyle: Interpolation = {
  background: colors.transparent,
  border: `${stroke.regular} solid ${colors.transparent}`,
  outline: "none",
  transition: "border-color 250ms ease-out",
};
const activeStyle: Interpolation<ThemeType> = (theme) => ({
  border: `${stroke.regular} solid ${theme.semanticColors.primary}`,
});
const contentTitleStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
});

export type PostPrayerContentCarouselProps = {
  sessions: PlayablePrayer[];
};

const makeKey = (id: number, idx: number): string => `item_${id}_${idx}`;
const parseKey = (key: string): [number, number] => {
  const pieces = key.split("_");
  return [parseInt(pieces[1]), parseInt(pieces[2])];
};

export type PostPrayerContentCarouselRef = {
  selectedSession: PlayablePrayer;
};

export const PostPrayerContentCarousel = forwardRef<
  PostPrayerContentCarouselRef,
  PostPrayerContentCarouselProps
>(({ sessions }, ref) => {
  const t = useTranslations();
  const [selectedSessionKey, setSelectedSessionKey] = useState<string | null>(
    makeKey(sessions?.[0]?.id ?? 0, 0),
  );
  const [mobilePageOffset, setMobilePageOffset] = useState<number>(75);
  const carouselApi = useCarousel({
    size: "s",
    hoverButtons: false,
    fixedWidth: false,
    pageOffset: [
      [mobilePageOffset, 150],
      [mobilePageOffset, 150],
    ],
    gap: [2, 4],
  });
  const intObs = useRef<IntersectionObserver | null>(null);
  const [activeId] = parseKey(selectedSessionKey);
  const divRef = useRef<HTMLDivElement>(null);
  const [isVisible] = useIntersectionObserverRefState({
    deps: [sessions],
    ref: divRef,
  });
  const [initialView, setInitialView] = useState<boolean>(true);
  const isDesktop = useIsDesktopViewport();

  useEffect(() => {
    const recalculate = () => {
      if (divRef.current && !isDesktop) {
        const space = divRef.current.getBoundingClientRect().width;
        // xs thumbnails are 56x56
        setMobilePageOffset(space / 2 - 28);
      }
    };
    recalculate();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", recalculate);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", recalculate);
      }
    };
  }, [isDesktop]);

  useImperativeHandle(ref, () => ({
    selectedSession: sessions.find((sess) => sess.id === activeId),
  }));

  useEffect(() => {
    if (isVisible && initialView) {
      const interval = setInterval(() => {
        const target = document.querySelector(`#${selectedSessionKey}`);

        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "center",
          });
          clearInterval(interval);
        }
      }, END_OF_BROWSER_ASYNC_QUEUE);
      setInitialView(false);
    }
  }, [isVisible, initialView]);

  useEffect(() => {
    if (carouselApi && typeof document !== "undefined") {
      if (!intObs.current)
        intObs.current = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const btn = entry.target.querySelector("button");
              if (btn) {
                if (entry.isIntersecting) {
                  btn.style.transform = `scale(${0.75 + 0.25 * entry.intersectionRatio})`;
                } else {
                  btn.style.transform = "scale(0.5)";
                }
              }
            }
          },
          {
            root: carouselApi.refParent.current,
            rootMargin: "0%",
            threshold: [0, 0.25, 0.5, 0.75, 1],
          },
        );
      for (const child of Array.from(carouselApi.refParent.current.children)) {
        intObs.current.observe(child);
      }
    }
  }, []);

  const handleClick = (e) => {
    e.currentTarget.scrollIntoView({ behavior: "smooth", inline: "center" });
    setSelectedSessionKey(e.currentTarget.id);
  };

  return (
    <div ref={divRef} css={containerStyle}>
      <Carousel
        carouselApi={carouselApi}
        containerCSS={emotionDynamics.mask({
          directions: ["to left", "to right"],
          spreads: [24, 24],
        })}
      >
        {sessions.map((sess, idx) => {
          const key = makeKey(sess.id, idx);
          return (
            <button key={key} id={key} onClick={handleClick} css={buttonStyle}>
              <Thumbnail
                size={"xs"}
                imageSrc={sess.images}
                title={sess.title}
                css={[
                  thumbnailStyle,
                  selectedSessionKey === key ? activeStyle : null,
                ]}
              />
            </button>
          );
        })}
      </Carousel>
      <Text as={"p"} type={"detail"} size={"xl"} css={contentTitleStyle}>
        {sessions.find((sess) => sess.id === activeId)?.title ?? null}
      </Text>
      <Text as={"p"} type={"quote"} size={"xl"}>
        {sessions.find((sess) => sess.id === activeId)?.shareable_quote ??
          t("sharing_default_text")}
      </Text>
    </div>
  );
});

PostPrayerContentCarousel.displayName = "PostPrayerContentCarousel";
